"use client"

import { useState, useEffect } from "react"
import api from "../../api/api"
import { useNavigate, useLocation } from "react-router-dom"
import { getEdinetUrl, getUsSecReportUrl } from "../../utils/commonUtils"
import Loading from "../../Loading"
import { useDashboardContext } from "../../../context/DashboardContext"
import { ExternalLink, Calendar, FileText } from "lucide-react"

const SecurityReportTable = ({ secCode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [securityReports, setSecurityReports] = useState(null)
  const { state: dashboardState } = useDashboardContext()
  const [isJpx, setIsJpx] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  // 画面サイズの変更を検知
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIfMobile()
    window.addEventListener("resize", checkIfMobile)

    return () => {
      window.removeEventListener("resize", checkIfMobile)
    }
  }, [])

  useEffect(() => {
    const fetchSecRports = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/security-reports/${secCode}/`, {})
        setSecurityReports({
          page: response.data.page,
          num: response.data.num_of_data,
          data: response.data.results,
        })
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          // 未認証の場合、ログイン画面にリダイレクト
          navigate("/login", { state: { from: location.pathname } })
        } else {
          console.error("Error fetching security reports:", error)
        }
      }
    }

    const getExchange = () => {
      if (dashboardState.companyInfo) {
        const companyInfo = dashboardState.companyInfo
        return companyInfo.exchangeshortname
      } else {
        return
      }
    }

    fetchSecRports()
    const exchange = getExchange()
    if (exchange === "JPX") {
      setIsJpx(true)
    } else {
      setIsJpx(false)
    }
  }, [secCode, navigate, location.pathname, dashboardState.companyInfo])

  if (!securityReports) {
    return <Loading />
  }

  const toTerm = (term) => {
    if (term === "FY") {
      return "通期"
    } else if (["Q1", "Q2", "Q3", "Q4"].includes(term)) {
      return term
    }
  }

  const toDate = (dateString) => {
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0") // 月は0始まりなので+1
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}` // ローカルタイムでYYYY-MM-DDを返す
  }

  const toYYYYMMDD = (dateString) => {
    const date = toDate(dateString)
    return date.replace(/-/g, "") // ハイフンを取り除いて YYYYMMDD にする
  }

  const handleUsSecReportClick = async (e, secCode, submitDate, docId) => {
    e.preventDefault()
    const url = getUsSecReportUrl(secCode, submitDate, docId)
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl, "_blank")
    } catch (error) {
      console.error("Failed to open SEC report:", error)
    }
  }

  return (
    <div className="dashboard-row max-h-[600px] overflow-x-hidden">
      <h3>有価証券報告書情報</h3>

      {isMobile ? (
        // モバイル表示 - カードレイアウト
        <div className="space-y-4 max-h-[500px] overflow-y-auto overflow-x-hidden px-2">
          {securityReports.data.map((data) => (
            <div key={data.doc_id} className="border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-1 text-sm font-medium text-gray-500 mb-2">
                <Calendar size={14} />
                <span>{toDate(data.submit_date)}</span>
              </div>

              <div className="mb-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {toTerm(data.type_of_period)}
                </span>
              </div>

              {isJpx ? (
                <a
                  href={getEdinetUrl(toYYYYMMDD(data.submit_date), data.doc_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-800 font-medium flex items-start gap-1"
                >
                  <FileText size={16} className="flex-shrink-0 mt-1" />
                  <span className="flex-1">
                    {data.document_name} - {data.term}
                  </span>
                  <ExternalLink size={16} className="flex-shrink-0 mt-1" />
                </a>
              ) : (
                <a
                  href="#"
                  onClick={(e) => handleUsSecReportClick(e, secCode, data.submit_date, data.doc_id)}
                  className="text-gray-600 hover:text-gray-800 font-medium flex items-start gap-1"
                >
                  <FileText size={16} className="flex-shrink-0 mt-1" />
                  <span className="flex-1">
                    {data.document_name} - {data.term}
                  </span>
                  <ExternalLink size={16} className="flex-shrink-0 mt-1" />
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        // デスクトップ表示 - テーブルレイアウト
        <div className="max-h-[500px] overflow-y-auto overflow-x-hidden">
          <table className="disclosure-table yuho-table w-full">
            <thead>
              <tr>
                <th className="text-left">開示日時</th>
                <th className="text-left">期間</th>
                <th className="text-left">タイトル</th>
              </tr>
            </thead>
            <tbody>
              {securityReports.data.map((data) => (
                <tr key={data.doc_id}>
                  <td className="text-left">{toDate(data.submit_date)}</td>
                  <td className="text-left">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      {toTerm(data.type_of_period)}
                    </span>
                  </td>
                  <td className="text-left">
                    {isJpx ? (
                      <a
                        href={getEdinetUrl(toYYYYMMDD(data.submit_date), data.doc_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline flex items-center gap-1"
                      >
                        <span>
                          {data.document_name} - {data.term}
                        </span>
                      </a>
                    ) : (
                      <a
                        href="#"
                        onClick={(e) => handleUsSecReportClick(e, secCode, data.submit_date, data.doc_id)}
                        className="hover:underline flex items-center gap-1"
                      >
                        <span>
                          {data.document_name} - {data.term}
                        </span>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default SecurityReportTable
